/**
 * Launcher V2 config page (plan U3) — pure, DOM/React-free logic.
 *
 * Deliberately split out of `components/PlaybooksConfigPage.tsx`: that file
 * (transitively, via `KebabMenu.tsx`) imports `@clay-run/glaze/dropdown`,
 * whose module top-level runs `class RedispatchedKeyboardEvent extends
 * KeyboardEvent {}` — a real `ReferenceError: KeyboardEvent is not defined`
 * under `bun:test`, which has no DOM globals (verified directly: importing
 * the component file from `__tests__/v2-config-page.test.tsx` crashed with
 * exactly that error before this split). Bun's isolated test runner has no
 * jsdom/happy-dom (`mini-apps/CLAUDE.md` "no DOM harness" convention), so any
 * test importing this page's logic must not pull in a component tree that
 * mounts `Dropdown`. This leaf module has zero Glaze/React imports, so it is
 * safe for both the component (which re-exports from here for its own JSX)
 * and the test file to import directly.
 */

import type { PlaybooksLibraryResponse } from './schemas';

/**
 * Cross-section search (R3): one query narrows all three sections
 * simultaneously by case-insensitive name substring, against the
 * already-fetched `/v2/library` payload — no new endpoint. An empty/blank
 * query restores every row.
 */
export function filterPlaybooksLibrary(
  library: PlaybooksLibraryResponse,
  query: string,
): PlaybooksLibraryResponse {
  const q = query.trim().toLowerCase();
  if (!q) return library;
  const matches = (name: string) => name.toLowerCase().includes(q);
  return {
    segmentations: library.segmentations.filter((s) => matches(s.name)),
    sequences: library.sequences.filter((s) => matches(s.name)),
    playbooks: library.playbooks.filter((p) => matches(p.name)),
  };
}

/**
 * The Terra-admin wildcard gate for playbook Delete (plan approach: "gated
 * on `useCurrentUser().roles` including wildcard-admin satisfaction").
 * Mirrors `src/auth/roles.ts`'s `satisfiesRole` wildcard semantics
 * (`hasRole(ctx, 'launcher_admin') || hasRole(ctx, 'admin')`) and
 * `web/app-home/src/lib/roles.ts`'s client-side `hasRequiredRole` — neither
 * is importable from a mini-app (`../../src/` and `../../web/` are both
 * off-limits per `mini-apps/CLAUDE.md`), so this is a small local
 * reimplementation of the same rule rather than a redeclared schema/type.
 *
 * A1's `useCurrentUser()` unstub already returns real `roles` (verified:
 * `mini-apps/_shared/hooks/useCurrentUser.ts` reads `data?.roles ?? []` from
 * the real `/api/me` response, not a hardcoded `[]`) — so this gates on
 * `roles` directly, with no `isAdmin`-fallback TODO needed.
 */
export function isLauncherAdmin(roles: readonly string[]): boolean {
  return roles.includes('launcher_admin') || roles.includes('admin');
}

/**
 * Parses the Config page's `?expand=<kind>:<id>` deep-link param (U1) —
 * the counterpart to `usage-sidebar-logic.ts`'s `originUrl`, which produces
 * exactly this format for a `'library'` origin
 * (`${basePath}/v2/config?expand=${targetKind}:${encodeURIComponent(id)}`).
 * `null` for an absent/empty/whitespace-only param or an unrecognized
 * `kind` — the caller (`PlaybooksConfigPage`) treats `null` as "nothing to
 * expand" rather than throwing on a malformed/hand-typed URL.
 */
export function parseConfigExpandParam(
  raw: string | null,
): { kind: 'sequence' | 'segmentation'; id: string } | null {
  if (raw === null) return null;
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const separatorIndex = trimmed.indexOf(':');
  if (separatorIndex === -1) return null;
  const kind = trimmed.slice(0, separatorIndex);
  const rawId = trimmed.slice(separatorIndex + 1);
  if (kind !== 'sequence' && kind !== 'segmentation') return null;
  if (rawId === '') return null;
  let id: string;
  try {
    id = decodeURIComponent(rawId);
  } catch {
    return null;
  }
  return { kind, id };
}

export type PlaybooksConfigRowKind = 'segmentation' | 'sequence' | 'playbook';
export type PlaybooksConfigRowAction = 'edit' | 'rename' | 'duplicate' | 'delete';

export interface PlaybooksConfigRowMenuItemDescriptor {
  action: PlaybooksConfigRowAction;
  label: string;
  danger?: boolean;
}

/**
 * QA round 7, U9 (item 16, OQ15) — kind-specific verbs so the Library row
 * and the ladder group menu ("Duplicate segmentation", `rung-menu.ts`) read
 * alike. All three kinds get the noun, not just segmentations, for
 * consistency across the Library's three sections.
 */
const KIND_NOUN: Record<PlaybooksConfigRowKind, string> = {
  segmentation: 'segment',
  sequence: 'sequence',
  playbook: 'playbook',
};

/**
 * Per-row-kind kebab item set (R3, revised 2026-09-11 QA round 2 — bug 1).
 * Segmentation/Sequence rows get Rename / Duplicate / Delete only: their row
 * header click already expands/collapses the row, so an "Edit" item wired to
 * that same toggle was a dead, redundant action. Playbook rows keep Edit
 * (its own distinct — if not-yet-built — action), plus Rename / Duplicate,
 * and Delete, which is Terra-admin-only and ABSENT (not present-and-disabled)
 * for a non-admin — matching the prototype's own hiding pattern for
 * admin-only controls (plan Key Decision 6).
 */
export function getConfigRowMenuItems(
  kind: PlaybooksConfigRowKind,
  opts: { isAdmin: boolean },
): PlaybooksConfigRowMenuItemDescriptor[] {
  const items: PlaybooksConfigRowMenuItemDescriptor[] = [];
  if (kind === 'playbook') {
    items.push({ action: 'edit', label: 'Edit' });
  }
  items.push(
    { action: 'rename', label: 'Rename' },
    { action: 'duplicate', label: `Duplicate ${KIND_NOUN[kind]}` },
  );
  const deleteAllowed = kind !== 'playbook' || opts.isAdmin;
  if (deleteAllowed) {
    items.push({ action: 'delete', label: `Delete ${KIND_NOUN[kind]}`, danger: true });
  }
  return items;
}
